import {
	Box,
	Button,
	CircularProgress,
	Grid,
	IconButton as IconBtn,
	Typography,
} from '@mui/material';
import {
	CareNoteSmallIcon,
	LeftArrowIcon,
	REOViewIcon,
} from '../../assets/icons/Icons';
import TextInput from '../../components/TextInput';
import { helperText } from '../../assets/theme/Theme';
import { FileTile, FilterTile } from './DataTiles';
import { MAIN_THEME_COLOR } from '../../assets/theme/Colors';
import { dateConverter } from '../../Utils/Helpers';
import IconButton from '../../components/IconButton';
import { useEffect, useRef } from 'react';
import CustomTextInput from '../../components/CustomTextInput';

const ITEM_FILTER = [
	'All',
	'Onboarding',
	'Initial Care Plan',
	'Optimized Care Plan',
];

function ResidentOverview({
	resident,
	residentFiles,
	errors,
	selectedItem,
	onFilterDataTile,
	onClickMore,
	goBack,
	onClickSave,
	isLoading,
	isSaveBtnDisabled = true,
	onChangeData,
	onDownloadResidentFile,
	careNotes,
}) {
	const scrollRef = useRef(null);

	useEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = 0;
		}
	}, [residentFiles]);

	return (
		<Grid
			container
			sx={{
				marginLeft: '30px',
				marginTop: '16px',
				padding: '16px',
				backgroundColor: '#FFFFFF',
			}}>
			<Grid
				item
				display={'flex'}
				alignItems={'center'}
				flexDirection={'row'}>
				<IconBtn disableRipple onClick={goBack}>
					<LeftArrowIcon />
				</IconBtn>
				<Typography
					color={'#191919'}
					fontSize={'18px'}
					marginLeft={'5px'}
					fontWeight={'600'}>
					Resident Profile
				</Typography>
			</Grid>
			<Grid
				container
				sx={{
					padding: '20px',
					margin: '10px',
					backgroundColor: '#FCFDFF',
					borderRadius: '6px',
					alignItems: 'center',
				}}>
				<REOViewIcon />
				<Typography
					color={'#324054'}
					fontSize={'16px'}
					marginLeft={'5px'}
					fontWeight={'600'}>
					Resident Overview
				</Typography>
				<Grid container>
					<Grid
						item
						xs={12}
						md={6}
						paddingTop={'10px'}
						flexDirection={'column'}>
						<CustomTextInput
							label={'Resident ID'}
							value={resident?.elderId}
							error={errors?.id}
							placeholder="N/A"
							disabled={true}
						/>
						<CustomTextInput
							label={'Resident Name'}
							value={resident?.name}
							error={errors?.name}
							onChange={value => onChangeData(value, 'name')}
							disabled={isSaveBtnDisabled}
						/>
						<CustomTextInput
							label={'Gender'}
							value={resident?.gender}
							error={errors?.gender}
							onChange={value => onChangeData(value, 'gender')}
							disabled={isSaveBtnDisabled}
							placeholder="N/A"
						/>
						<CustomTextInput
							label={'Age'}
							value={resident?.age}
							error={errors?.age}
							onChange={value => onChangeData(value, 'age')}
							disabled={isSaveBtnDisabled}
							placeholder="N/A"
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						<Grid
							container
							sx={{
								backgroundColor: '#FFFFFF',
								borderRadius: '6px',
								padding: '20px',
							}}>
							<Typography
								color={'#50565F'}
								fontSize={'14px'}
								fontWeight={'600'}>
								Uploaded Documents
							</Typography>
							<Grid container paddingY="10px" marginTop={'10px'}>
								{ITEM_FILTER.map((item, index) => {
									return (
										<FilterTile
											key={index}
											name={item}
											isSelected={item === selectedItem}
											onClick={() =>
												onFilterDataTile(item)
											}
										/>
									);
								})}
							</Grid>
							<Box
								ref={scrollRef}
								width={'100%'}
								sx={{
									marginTop: '10px',
									maxHeight: '180px',
									overflowY: 'auto',
									paddingBottom: '5px',
									scrollbarWidth: 'none', // Firefox
									'&::-webkit-scrollbar': {
										display: 'none',
									},
								}}>
								<Grid container rowGap={1}>
									{residentFiles.length > 0 ? (
										residentFiles.map((item, index) => {
											return (
												<FileTile
													key={index}
													name={
														item.key.split('_')[1]
													}
													addedDate={dateConverter(
														item.date,
													)}
													onDownload={() =>
														onDownloadResidentFile(
															item.key,
															item.type,
															true,
														)
													}
													onView={() =>
														onDownloadResidentFile(
															item.key,
															item.type,
														)
													}
												/>
											);
										})
									) : (
										<Grid
											container
											justifyContent={'center'}
											alignItems={'center'}
											marginTop={'50px'}>
											<Typography
												sx={{
													whiteSpace: 'normal',
													wordBreak: 'break-word',
													padding: '10px',
													fontSize: '16px',
													color: '#3D4A58',
												}}
												fontWeight={'500'}>
												No Files
											</Typography>
										</Grid>
									)}
								</Grid>
							</Box>
							<Grid container justifyContent={'flex-end'}>
								<IconBtn
									disableFocusRipple
									disableRipple
									onClick={onClickMore}>
									<Typography
										fontSize={'14px'}
										paddingY={'10px'}
										color={MAIN_THEME_COLOR}
										fontWeight={
											'500'
										}>{`More >>>`}</Typography>
								</IconBtn>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
				{!isSaveBtnDisabled && (
					<Grid
						container
						justifyContent={'flex-end'}
						paddingTop={'10px'}
						marginRight={'20px'}
						flexDirection={'row'}>
						{isLoading && (
							<Grid marginRight={'10px'} marginTop={'5px'}>
								<CircularProgress
									sx={{ color: '#28696D' }}
									size={'2rem'}
								/>
							</Grid>
						)}
						<Button
							onClick={goBack}
							sx={{
								paddingLeft: '15px',
								paddingRight: '15px',
								color: '#7F7C7C',
								border: '1px solid #7F7C7C',
								fontWeight: '600',
								textTransform: 'none',
								marginRight: '10px',
							}}>
							{'Cancel'}
						</Button>
						<IconButton text={'Save'} onClick={onClickSave} />
					</Grid>
				)}
			</Grid>
			<Grid
				container
				sx={{
					padding: '20px',
					margin: '10px',
					backgroundColor: '#FCFDFF',
					borderRadius: '6px',
					alignItems: 'center',
				}}>
				<CareNoteSmallIcon />
				<Typography
					color={'#324054'}
					fontSize={'16px'}
					marginLeft={'5px'}
					fontWeight={'600'}>
					Resident Care Notes
				</Typography>
				<Grid container paddingTop={'15px'}>
					{careNotes.length > 0 ? (
						careNotes.map((note, index) => {
							return (
								<Typography
									key={index}
									sx={{
										width: '100%',
										paddingX: '10px',
										paddingY: '5px',
										fontSize: '14px',
										color: '#3D4A58',
									}}>
									{note}
								</Typography>
							);
						})
					) : (
						<Grid
							container
							justifyContent={'center'}
							alignItems={'center'}
							marginTop={'50px'}>
							<Typography
								sx={{
									whiteSpace: 'normal',
									wordBreak: 'break-word',
									padding: '10px',
									fontSize: '16px',
									color: '#3D4A58',
								}}
								fontWeight={'500'}>
								No Care Notes
							</Typography>
						</Grid>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
}

export default ResidentOverview;
